/**
 * Quality gate: defines the minimum standard a recipe must meet
 * to be displayed to end users. Recipes below this bar are hidden
 * and logged for admin remediation.
 */

interface RecipeLike {
  id: string;
  title?: string | null;
  ingredients?: string[] | unknown;
  preparation?: string | null;
  prep_time?: string | null;
  total_time?: string | null;
  time?: string | null;
  image?: string | null;
  description?: string | null;
  /** Recipes that lack ingredients/preparation fields are lightweight previews — skip gating */
  _isPreview?: boolean;
}

export interface QualityResult {
  pass: boolean;
  issues: string[];
}

/** Check if at least one ingredient contains a numeric quantity */
function hasQuantities(ingredients: string[]): boolean {
  return ingredients.some((ing) => /\d/.test(ing));
}

/** Check if preparation has meaningful steps (not just a single short sentence) */
function hasAdequateSteps(preparation: string | null | undefined): boolean {
  if (!preparation) return false;
  const text = String(preparation).trim();
  if (text.length < 30) return false;
  // Count logical steps (split by newlines/pipes/numbered patterns)
  const steps = text
    .split(/\n|\|/)
    .map((s) => s.trim())
    .filter((s) => s.length > 5);
  return steps.length >= 1;
}

/** Check if any time field is present */
function hasTimeInfo(r: RecipeLike): boolean {
  return !!(
    (r.total_time && String(r.total_time).trim()) ||
    (r.prep_time && String(r.prep_time).trim()) ||
    (r.time && String(r.time).trim())
  );
}

/**
 * Evaluates a single recipe against the minimum quality bar.
 * Does NOT fire telemetry — caller is responsible.
 */
export function evaluateRecipeQuality(recipe: RecipeLike): QualityResult {
  const issues: string[] = [];

  // 1. Must have a title
  if (!recipe.title || String(recipe.title).trim().length < 3) {
    issues.push("missing_title");
  }

  // 2. Must have ingredients
  const ings = Array.isArray(recipe.ingredients) ? recipe.ingredients.map(String).filter(Boolean) : [];
  if (ings.length === 0) {
    issues.push("missing_ingredients");
  } else if (!hasQuantities(ings)) {
    issues.push("missing_quantities");
  }

  // 3. Must have preparation steps
  if (!hasAdequateSteps(recipe.preparation)) {
    issues.push("missing_steps");
  }

  // 4. Must have at least one time field
  if (!hasTimeInfo(recipe)) {
    issues.push("missing_time");
  }

  // 5. Image is tracked but NOT a blocking issue (user generates later)
  // if (!recipe.image || String(recipe.image).trim().length === 0) {
  //   issues.push("missing_image");
  // }

  return { pass: issues.length === 0, issues };
}

/**
 * Filters a list of recipes, removing those that don't pass the quality gate.
 * Fires telemetry for hidden recipes (batched per render, deduped).
 */
const _reported = new Set<string>();

export function filterByQualityGate<T extends RecipeLike>(recipes: T[]): T[] {
  const passed: T[] = [];

  for (const recipe of recipes) {
    const result = evaluateRecipeQuality(recipe);
    if (result.pass) {
      passed.push(recipe);
    } else {
      if (!_reported.has(recipe.id)) {
        _reported.add(recipe.id);
      }
    }
  }

  return passed;
}
